/**
 * 订单状态定义。
 *
 * 语义见 docs/03-order-lifecycle.md §3.1。关键约束:
 * 照片是状态转换的**门槛** —— ACCEPTED → PICKED_UP 和 → DELIVERED
 * 在拍照要求开启时,没有照片就不允许转换,订单停在原状态。
 */
export const ORDER_STATUSES = [
  'DRAFT', // 草稿:导入预览阶段的临时单
  'PENDING', // 待派单:已建单无司机。这是订单池
  'ASSIGNED', // 待接单:已指派,司机尚未响应
  'ACCEPTED', // 已接单:司机确认,尚未取货
  'PICKED_UP', // 已取货:司机已从发货方取到货
  'IN_TRANSIT', // 配送中:可选,租户配置 enableInTransit 开启
  'DELIVERED', // 已送达:终态
  'FAILED', // 投递失败:货还在司机手上,待改派或退回
  'RETURNED', // 已退回:终态
  'CANCELLED', // 已取消:终态
] as const;

export type OrderStatus = (typeof ORDER_STATUSES)[number];

/** 终态:不能再转换出去 */
export const TERMINAL_STATUSES = ['DELIVERED', 'RETURNED', 'CANCELLED'] as const satisfies readonly OrderStatus[];

export type TerminalStatus = (typeof TERMINAL_STATUSES)[number];

/**
 * 「走到哪一步了」—— 只用来判断**某个动作是不是已经过去了**。
 *
 * ★ 不是业务排序,别拿它排列表:`FAILED` 排在 `PICKED_UP` 后面是因为
 *   「送过了没送成」意味着取货那一步确实已经发生,不是因为它「更靠后」。
 *
 * ★ `CANCELLED` 故意给 0。取消不是往前走,是把这一单作废 ——
 *   而司机手上可能还有货。一条迟到的取货重试落到一张已取消的单上时,
 *   **必须**照常报冲突让人知道,不能当成「已经过去了」咽下去。
 *   (见 docs/05-driver-app.md §05.6 和 flow.offline 那条用例。)
 */
export const STATUS_PROGRESS: Record<OrderStatus, number> = {
  DRAFT: 0,
  PENDING: 1,
  ASSIGNED: 2,
  ACCEPTED: 3,
  PICKED_UP: 4,
  IN_TRANSIT: 5,
  FAILED: 6,
  DELIVERED: 7,
  RETURNED: 7,
  CANCELLED: 0,
};

export function isTerminal(status: OrderStatus): status is TerminalStatus {
  return (TERMINAL_STATUSES as readonly OrderStatus[]).includes(status);
}

/**
 * 会画在地图上的状态。
 *
 * ★ 地图是**干活的工具**,不是历史记录:调度员看地图是为了回答
 *   「现在还有什么没送到、谁在哪」。已送达、已退回、已取消的单
 *   画上去只会挤占屏幕 —— 一天下来终态单会是在途单的几十倍,
 *   到那时地图上什么都看不出来了。
 *
 * ★ `DRAFT` 也不画:导入预览阶段的临时单,还没真正存在。
 *
 * ★ `FAILED` 要画。它听起来像结束了,其实**货还在司机手上**,
 *   是最需要调度盯着的一类。
 */
export const MAPPABLE_STATUSES = [
  'PENDING',
  'ASSIGNED',
  'ACCEPTED',
  'PICKED_UP',
  'IN_TRANSIT',
  'FAILED',
] as const satisfies readonly OrderStatus[];

export type MappableStatus = (typeof MAPPABLE_STATUSES)[number];

export function isMappable(status: string): status is MappableStatus {
  return (MAPPABLE_STATUSES as readonly string[]).includes(status);
}

/**
 * 状态 → 图钉颜色。
 *
 * ★ 一份,两端共用。之前管理端和司机端各有一份一模一样的表 ——
 *   而且**两份都把「已取货」和「配送中」画成同一个绿**:
 *   开了「配送中」这个状态的租户,在地图上根本分不出货刚拿到手
 *   还是已经在往收件人去的路上。
 *
 * ★ 颜色顺着流程走冷 → 暖 → 绿(待派单的灰 → 待接单的琥珀 →
 *   已接单的蓝 → 已取货的青 → 配送中的绿),失败是唯一的红。
 *   相邻状态之间的色相差得开,一屏几十个点时才分得清。
 */
export const STATUS_PIN_COLOR: Record<MappableStatus, string> = {
  PENDING: '#94a3b8', // slate —— 还没人管
  ASSIGNED: '#f59e0b', // amber —— 等司机点头,最该被注意到
  ACCEPTED: '#3b82f6', // blue —— 有人接了
  PICKED_UP: '#14b8a6', // teal —— 货到手
  IN_TRANSIT: '#22c55e', // green —— 在路上
  FAILED: '#ef4444', // red —— 唯一的红,货还在司机手上
};

/**
 * 司机「手上有单、在跑」的状态 —— 只有这时才采集他的位置。
 *
 * ★ 这是对员工的定位监控,采多少要有边界:空闲、下线时一个点都不采。
 *   派了还没点接单的(ASSIGNED)不算 —— 他还没答应这一单。
 *   投递失败(FAILED)也不算:货虽然还在车上,但可能拖几天才退回,
 *   算进去就成了「失败一单,被跟踪好几天」。
 *
 * 司机端(什么时候开始采)和服务端(收不收)都用这一份。
 */
export const TRACKING_STATUSES = ['ACCEPTED', 'PICKED_UP', 'IN_TRANSIT'] as const satisfies readonly OrderStatus[];

/** 司机手上有货的状态 —— 取消时必须交代货物去向 */
export function driverHoldsGoods(status: OrderStatus): boolean {
  return status === 'PICKED_UP' || status === 'IN_TRANSIT' || status === 'FAILED';
}

/**
 * 投递失败的细分原因。
 * 对应司机端的预置选项(docs/14-driver-ux-spec.md §14.6),不让司机打字。
 */
export const FAIL_REASONS = [
  'NO_ONE_HOME', // 无人接收
  'PHONE_UNREACHABLE', // 电话打不通
  'WRONG_ADDRESS', // 地址错误 / 找不到
  'REFUSED', // 客户拒收
  'NO_ACCESS', // 无法进入(小区/写字楼)
  'DAMAGED', // 货物破损
  'RESCHEDULED', // 客户要求改约
  'OTHER', // 其他(需说明)
] as const;

export type FailReason = (typeof FAIL_REASONS)[number];

/** 失败后货物的去向 —— 必答,否则会出现「货在司机手上但没人知道」 */
export const FAIL_DISPOSITIONS = ['RETRY', 'RETURN_TO_SENDER'] as const;
export type FailDisposition = (typeof FAIL_DISPOSITIONS)[number];

/** 签收方式 */
export const SIGNED_BY_TYPES = [
  'SELF', // 本人签收
  'PROXY', // 他人代收,需填姓名和关系
  'LEFT_AT_LOCATION', // 放在指定位置 —— 强制拍照,不受开关影响
] as const;

export type SignedByType = (typeof SIGNED_BY_TYPES)[number];

/** 放置位置的预置选项,同样不让司机打字 */
export const DROP_LOCATIONS = ['DOOR', 'RECEPTION', 'SECURITY', 'LOCKER', 'OTHER'] as const;
export type DropLocation = (typeof DROP_LOCATIONS)[number];

/**
 * 对外展示的状态映射。
 * 收件人看到的追踪页不能暴露内部状态,也用于第三方回调的反向映射。
 */
export const PUBLIC_STATUS_MAP: Record<OrderStatus, string> = {
  DRAFT: 'PENDING',
  PENDING: 'PENDING',
  ASSIGNED: 'PENDING',
  ACCEPTED: 'PENDING',
  PICKED_UP: 'IN_TRANSIT',
  IN_TRANSIT: 'IN_TRANSIT',
  DELIVERED: 'DELIVERED',
  FAILED: 'EXCEPTION',
  RETURNED: 'RETURNED',
  CANCELLED: 'CANCELLED',
};
